<template>
  <TContainer class="mt-8 md:mt-12 relative">
    <TOverlay :show="isCreating" class="z-20" />

    <div class="clear-both">
      <div class="float-left w-full lg:w-2/3 lg:pr-2">
        <div class="">
          <ApiResponseError
            v-if="hasError"
            :api-response-error="formError"
            class="mb-8"
          />

          <template v-if="!createSuccess">
            <div v-if="cottageContactDealPage">
              <TTitle tag="h1" size="2xl" classes="mb-2">
                {{ t('views.cottage-contact-deal.title') }}
              </TTitle>
              <TText
                v-if="
                  cottageContactDealPage.owner.answer &&
                  cottageContactDealPage.owner.answer.rate !== null
                "
                classes="mb-2"
              >
                {{
                  cottageContactDealPage.owner.answer.rate > 0
                    ? t('views.cottage-contact-deal.answerRate', {
                        rate: cottageContactDealPage.owner.answer.rate,
                        range: t(
                          `range.${cottageContactDealPage.owner.answer.range}`,
                        ),
                        value: cottageContactDealPage.owner.answer.value,
                        unit: t(
                          `time.${cottageContactDealPage.owner.answer.unit}`,
                          cottageContactDealPage.owner.answer.value,
                        ),
                      })
                    : t('views.cottage-contact-deal.noAnswerRate')
                }}
              </TText>
              <UButton
                color="primary"
                variant="link"
                :label="t('views.cottage-contact-whatsapp.cottageLink')"
                :to="
                  routes().cottageDetail({
                    slug: cottageContactDealPage.slug,
                    province: cottageContactDealPage.province.slug,
                  })
                "
              ></UButton>
              <TSeparator class="mt-6 mb-8" />

              <CottageContactDealInfo
                :item="cottageContactDealPage"
              ></CottageContactDealInfo>
            </div>
          </template>
        </div>
      </div>
      <div class="float-right w-full lg:w-1/3 lg:pl-2">
        <div class="">
          <template v-if="!createSuccess">
            <div v-if="cottageContactDealPage">
              <CottageContactDealResume :item="cottageContactDealPage" />
            </div>
          </template>
        </div>
      </div>
      <div class="float-left w-full lg:w-2/3 lg:pr-2 mb-20">
        <div class="">
          <template v-if="!createSuccess">
            <div v-if="cottageContactDealPage">
              <CottageContactDealForm
                class="mt-4 lg:mt-6"
                :item="cottageContactDealPage"
                @submit="onSubmit"
              />
            </div>
          </template>

          <CottageContactDealPageSuccess
            v-if="createSuccess && cottageContactDealPage"
            :id="cottageContactDealPage.id"
          />
        </div>
      </div>
    </div>
  </TContainer>
</template>

<script setup lang="ts">
// Composables
import { useCottageContactDealPage } from '../composables/useCottageContactDealPage'
import {
  sendGa4ContactEvent,
  sendCottageConversionAll,
} from '~~/modules/integrations/ga4/composables/useAnalyticsGa4Composable'
import { sendAdsContact } from '~~/modules/integrations/ads/composables/useAnalyticsAdsComposable'
import {
  sendConnectifPurchase,
  sendConnectifPageProductCart,
} from '~~/modules/integrations/connectif/composables/useConnectifComposable'

// Entities
import type { CottageContactDealPage } from '~~/modules/views/cottage-contact-deal/entities/CottageContactDealPage'
import type {
  CottageContactDealPageCreateForm,
  CottageContactDealPageCreateResponse,
} from '../entities/CottageContactDealPageCreate'
import type { CottageContactDealPagePricesDates } from '../entities/CottageContactDealPagePrices'

// Composables
const { sendFormData } = useCottageContactDealPage()
const { setCookieRuralityCottageContact } = useCookiesRurality()

// Component state
const isCreating = ref(false)
const hasError = ref(false)
const createSuccess = ref(false)

// Props
interface propsInterface {
  cottageContactDealPage: CottageContactDealPage
}
const props = defineProps<propsInterface>()

// Cart ID ref
const ConnectifCartId: Ref<string | null> = ref(null)
const ConnectifTotalPrice: Ref<number> = ref(
  props.cottageContactDealPage.deal.total.amount,
)

// Constants
let formError: ApiResponseError | null = null

const onSubmit = async (ev: CottageContactDealPageCreateForm) => {
  isCreating.value = true
  hasError.value = false
  if (props.cottageContactDealPage) {
    const datesParse: CottageContactDealPagePricesDates = {
      start: new Date(props.cottageContactDealPage.deal.dateIn),
      end: new Date(props.cottageContactDealPage.deal.dateOut),
    }

    const responseSendFormData = await sendFormData({
      createForm: ev,
      cottageContactDealPage: props.cottageContactDealPage,
      dates: datesParse,
      people: props.cottageContactDealPage.deal.people,
    })
    if (responseSendFormData.apiResponseError) {
      formError = responseSendFormData.apiResponseError
      hasError.value = true
      isCreating.value = false
    } else if (responseSendFormData.apiResponseSuccess) {
      onSuccess(responseSendFormData.apiResponseSuccess, ev)
    }
  }
}

const onSuccess = (
  cottageContactDealPageCreateResponse: CottageContactDealPageCreateResponse,
  formData: CottageContactDealPageCreateForm,
) => {
  if (props.cottageContactDealPage) {
    setDataRepository(formData)

    sendGa4ContactEvent({
      province: props.cottageContactDealPage.province.name,
      method: 'email',
    })
    sendCottageConversionAll()
    if (cottageContactDealPageCreateResponse) {
      sendAdsContact({
        email: cottageContactDealPageCreateResponse.traveler.email,
        phone: cottageContactDealPageCreateResponse.traveler.phone,
      })
    }

    // Connectif
    if (
      ConnectifCartId.value &&
      cottageContactDealPageCreateResponse.traveler.email
    ) {
      sendConnectifPurchase(
        {
          ...props.cottageContactDealPage,
          cartId: ConnectifCartId.value,
          productPrice: ConnectifTotalPrice.value,
          paymentMethod: 'Contact',
        },
        {
          entityInfo: {
            primaryKey: cottageContactDealPageCreateResponse.traveler.email,
          },
        },
      )
    }

    isCreating.value = false
    createSuccess.value = true
  }
}

const setDataRepository = (formData: CottageContactDealPageCreateForm) => {
  if (formData.message.length < 1) {
    formData.message = '...'
  }

  setCookieRuralityCottageContact({
    message: formData.message,
    traveler_email: formData.email,
    traveler_name: formData.name,
    traveler_phone: formData.phone,
  })
}

// Initialize cart and send tracking events
onMounted(() => {
  if (props.cottageContactDealPage) {
    ConnectifCartId.value = new Date().getTime().toString()

    sendConnectifPageProductCart({
      ...props.cottageContactDealPage,
      cartId: ConnectifCartId.value,
      productPrice: ConnectifTotalPrice.value,
      paymentMethod: 'Contact',
    })
  }
})
</script>
